import { faBarsFilter } from '@fortawesome/pro-light-svg-icons';
import {
  Action,
  Button,
  cn,
  FilterBar,
  Icon,
  SearchInput,
  SearchSpinner,
  SortMenu,
} from 'corso-admin-ui';
import { CrewMerchantUi } from 'corso-types';
import { useCallback, useState } from 'react';
import { DataExportForm } from './report/DataExportForm';
import { LOCAL_VIEW_ID, useSearchState } from './SearchContext';
import { useUpdateSearchView } from './useSearchViewApi';
import { CreateView, Views } from './Views';

type SearchControlBarProps = {
  isSearchPending?: boolean;
  className?: string;
};

function ViewTopBar({ isSearchPending }: { isSearchPending?: boolean }) {
  const [state, dispatch] = useSearchState();
  const onViewSelect = useCallback(
    (view: CrewMerchantUi.SearchView | null) =>
      dispatch({ type: 'viewChange', view }),
    [dispatch],
  );

  return (
    <>
      <div className="flex max-w-full grow items-center gap-2 overflow-x-auto">
        <Views
          entity={state.kind}
          selectedView={state.view}
          onViewEdit={() => dispatch({ type: 'toggleFilters', show: true })}
          onViewSelect={onViewSelect}
        />
      </div>
      <div className="flex items-center gap-2">
        <CreateView
          view={{ kind: state.kind, filters: {} }}
          onCreate={onViewSelect}
          triggerContent={<Icon icon="add" accessibilityLabel="Add View" />}
          triggerVariant="ghost"
          triggerIcon
        />
        <SearchSpinner show={isSearchPending} />

        <Button
          className="min-h-8 text-xs"
          onClick={() => dispatch({ type: 'toggleFilters', show: true })}
          data-testid="toggle"
        >
          <div className="flex items-center gap-1">
            <span className="sr-only">Search and filter</span>
            <Icon icon="search" />
            <Icon icon={faBarsFilter} />
          </div>
        </Button>
      </div>
    </>
  );
}

function FilterTopBar({ isSearchPending }: { isSearchPending?: boolean }) {
  const [state, dispatch] = useSearchState();

  const { mutate: saveChanges } = useUpdateSearchView((updatedView) => {
    dispatch({ type: 'viewChange', view: updatedView });
  });

  const { view: selectedView } = state;

  return (
    <div className="flex grow items-center gap-2">
      <SearchInput
        defaultValue={state.view.filters.searchTerm ?? ''}
        onSearch={(value) =>
          dispatch({
            type: 'filterChange',
            filter: { ...state.filterDefinition.filters.searchTerm, value },
          })
        }
        placeholder={
          selectedView.id === LOCAL_VIEW_ID
            ? 'Search all'
            : `Searching in ${selectedView.name}`
        }
      />
      <SearchSpinner show={isSearchPending} />

      <Button
        className="min-h-8 text-xs"
        onClick={() => dispatch({ type: 'toggleFilters', show: false })}
        data-testid="toggle"
      >
        Cancel
      </Button>
      {state.view.id === LOCAL_VIEW_ID ? (
        <CreateView
          view={{ kind: state.kind, filters: state.view.filters }}
          onCreate={(view) => dispatch({ type: 'viewChange', view })}
          triggerContent="Save as"
        />
      ) : (
        <Button
          className="min-h-8 text-xs"
          disabled={!state.viewModified}
          onClick={() => {
            saveChanges(state.view);
          }}
        >
          Save
        </Button>
      )}
    </div>
  );
}

export default function SearchControlBar({
  isSearchPending,
  className,
}: SearchControlBarProps) {
  const [state, dispatch] = useSearchState();
  const [showExport, setShowExport] = useState(false);

  // filter out the search term from the filters since it's handled separately
  const { searchTerm, ...activeFilters } = state.view.filters;
  const { searchTerm: searchDefinition, ...filters } =
    state.filterDefinition.filters;

  return (
    <div
      className={cn('w-full divide-y divide-neutral-200 bg-white', className)}
    >
      <div className="flex items-center gap-2 p-2">
        {state.showFilters ? (
          <FilterTopBar isSearchPending={isSearchPending} />
        ) : (
          <ViewTopBar isSearchPending={isSearchPending} />
        )}

        <SortMenu
          options={state.sortOptions}
          value={state.orderBy}
          onChange={(orderBy) => dispatch({ type: 'sortChange', orderBy })}
        />
        {state.kind !== 'Order' && state.kind !== 'Product' && (
          <>
            <Action
              className="min-h-8"
              icon="download"
              onClick={() => setShowExport(true)}
              disabled={isSearchPending}
              accessibilityLabel="Export"
            />
            {showExport && (
              <DataExportForm
                onClose={() => setShowExport(false)}
                show={showExport}
                entity={state.kind}
              />
            )}
          </>
        )}
      </div>

      {state.showFilters && (
        <FilterBar
          data-testid="filters"
          className={cn('p-2 duration-200', 'animate-in fade-in')}
          filters={filters}
          activeFilters={activeFilters}
          onChange={(change) =>
            dispatch({
              type: 'filterChange',
              filter: change,
            })
          }
          onRemoveFilters={(ids) => dispatch({ type: 'removeFilters', ids })}
        />
      )}
    </div>
  );
}
